/**
 * useMutation hook - Thực hiện mutations (POST, PUT, PATCH, DELETE)
 */

'use client';

import { useState, useCallback } from 'react';
import type {
  UseMutationOptions,
  UseMutationResult,
  ApiError,
} from '../types';
import type { HttpClient } from '../https/client';

type MutationMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export function useMutation<TData = unknown, TVariables = unknown>(
  client: HttpClient,
  method: MutationMethod,
  path: string,
  options?: UseMutationOptions<TData, TVariables>
): UseMutationResult<TData, TVariables> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const executeMutation = useCallback(
    async (
      variables: TVariables,
      overrideOptions?: UseMutationOptions<TData, TVariables>
    ): Promise<TData | undefined> => {
      setIsLoading(true);
      setError(null);

      const mergedOptions = { ...options, ...overrideOptions };

      try {
        let result: TData;

        switch (method) {
          case 'POST':
            result = await client.post<TData, TVariables>(path, variables);
            break;
          case 'PUT':
            result = await client.put<TData, TVariables>(path, variables);
            break;
          case 'PATCH':
            result = await client.patch<TData, TVariables>(path, variables);
            break;
          case 'DELETE':
            result = await client.delete<TData, TVariables>(path, variables);
            break;
          default:
            throw new Error(`Unsupported method: ${method}`);
        }

        if (mergedOptions.onSuccess) {
          await mergedOptions.onSuccess(result, variables);
        }

        return result;
      } catch (err) {
        const apiError = err as ApiError;

        setError(apiError);

        if (mergedOptions.onError) {
          await mergedOptions.onError(apiError, variables);
        }

        throw apiError;
      } finally {
        setIsLoading(false);
      }
    },
    [client, method, path, options]
  );

  const mutate = useCallback(
    async (variables: TVariables) => {
      try {
        return await executeMutation(variables);
      } catch {
        // Error đã được handle trong executeMutation
        return undefined;
      }
    },
    [executeMutation]
  );

  const mutateAsync = useCallback(
    async (
      variables: TVariables,
      overrideOptions?: UseMutationOptions<TData, TVariables>
    ) => {
      return executeMutation(variables, overrideOptions);
    },
    [executeMutation]
  );

  const reset = useCallback(() => {
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    mutate,
    mutateAsync,
    isLoading,
    error,
    reset,
  };
}
